// Brand-matched icon set. Stroke-based, currentColor, 24×24 viewBox so they
// inherit color/size from their parent. Designed to match the existing
// Tailwind icon vocabulary: stroke-width 2, round line caps/joins.

import type { SVGProps } from 'react';

type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const baseProps = (size: number) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
});

export function CompassIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <circle cx="12" cy="12" r="10" />
      <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
    </svg>
  );
}

export function SparkleIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <path d="M12 3 L13.5 9 L19.5 10.5 L13.5 12 L12 18 L10.5 12 L4.5 10.5 L10.5 9 Z" />
      <path d="M19 17l.7 2.3 2.3.7-2.3.7-.7 2.3-.7-2.3-2.3-.7 2.3-.7Z" />
    </svg>
  );
}

export function TargetIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" />
    </svg>
  );
}

export function MicrophoneIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 11v1a7 7 0 0 0 14 0v-1" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="8" y1="22" x2="16" y2="22" />
    </svg>
  );
}

export function PinIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <path d="M12 22s8-7.5 8-13a8 8 0 0 0-16 0c0 5.5 8 13 8 13z" />
      <circle cx="12" cy="9" r="3" />
    </svg>
  );
}

export function BriefcaseIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
      <line x1="2" y1="13" x2="22" y2="13" />
    </svg>
  );
}

export function FlameIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <path d="M12 2c.5 3 2 5 4 6.5 2 1.5 3 3.5 3 6 0 3.6-3 6.5-7 6.5s-7-2.9-7-6.5c0-1.4.5-2.7 1.4-3.7-.4 1.5.8 2.7 1.6 1.7 1-1.3 1-3.5 0-5.5 1.5.5 3 .5 4-.5 1-1 1-3 0-4.5z" />
    </svg>
  );
}

export function ChipIcon({ size = 24, ...rest }: IconProps) {
  // Used as the "AI on your mind" icon — circuit-board / processor look.
  return (
    <svg {...baseProps(size)} {...rest}>
      <rect x="5" y="5" width="14" height="14" rx="2" />
      <rect x="9" y="9" width="6" height="6" />
      <line x1="9" y1="2" x2="9" y2="5" />
      <line x1="15" y1="2" x2="15" y2="5" />
      <line x1="9" y1="19" x2="9" y2="22" />
      <line x1="15" y1="19" x2="15" y2="22" />
      <line x1="2" y1="9" x2="5" y2="9" />
      <line x1="2" y1="15" x2="5" y2="15" />
      <line x1="19" y1="9" x2="22" y2="9" />
      <line x1="19" y1="15" x2="22" y2="15" />
    </svg>
  );
}

export function StarFilledIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...rest}
    >
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
  );
}

export function ScalesIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <line x1="12" y1="3" x2="12" y2="21" />
      <line x1="4" y1="21" x2="20" y2="21" />
      <path d="M6 8l-3 7a4 4 0 0 0 6 0L6 8z" />
      <path d="M18 8l-3 7a4 4 0 0 0 6 0L18 8z" />
      <line x1="12" y1="6" x2="6" y2="8" />
      <line x1="12" y1="6" x2="18" y2="8" />
    </svg>
  );
}

export function AwardIcon({ size = 24, ...rest }: IconProps) {
  // Used for "your strongest score" — laurel/award look.
  return (
    <svg {...baseProps(size)} {...rest}>
      <circle cx="12" cy="9" r="6" />
      <polyline points="8.21 13.89 7 22 12 19 17 22 15.79 13.88" />
    </svg>
  );
}

export function GroupIcon({ size = 24, ...rest }: IconProps) {
  // 850k+ people / community indicator.
  return (
    <svg {...baseProps(size)} {...rest}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

export function ShieldIcon({ size = 24, ...rest }: IconProps) {
  return (
    <svg {...baseProps(size)} {...rest}>
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <polyline points="9 12 11 14 15 10" />
    </svg>
  );
}
